<template>
  <div v-if="rangeMin < rangeMax" class="cf__item" :class="{ active: isActive }">
    <div
      class="cf__title-wrapper"
      @click="isActive = !isActive"
      @keydown.enter.space.prevent="isActive = !isActive"
      tabindex="0"
      role="button"
      :aria-expanded="isActive"
      aria-controls="filter-panel-price"
      :aria-label="isActive ? 'Collapse price filter' : 'Expand price filter'"
    >
      <div class="cf__title">
        price
        <span v-if="isRefined">(1)</span>
      </div>
      <svg class="cf__icon">
        <use xlink:href="#icon-prev"></use>
      </svg>
    </div>
    <div class="cf__content-outer-wrapper" id="filter-panel-price" :aria-hidden="!isActive">
      <div class="cf__content-wrapper">
        <div class="price-slider">
          <div class="price-slider__runway">
            <div class="price-slider__track">
              <div class="price-slider__fill" :style="fillStyle"></div>
            </div>
            <input
              type="range"
              class="price-slider__input price-slider__input--min"
              :min="rangeMin"
              :max="rangeMax"
              :value="currentMin"
              aria-label="Minimum price"
              @input="onMinInput"
              @change="commitRange"
            >
            <input
              type="range"
              class="price-slider__input price-slider__input--max"
              :min="rangeMin"
              :max="rangeMax"
              :value="currentMax"
              aria-label="Maximum price"
              @input="onMaxInput"
              @change="commitRange"
            >
          </div>
          <div class="price-slider__values">
            <span class="price-slider__value price-slider__value--min" :style="minLabelStyle">
              $ {{ Math.round(currentMin) }}
            </span>
            <span class="price-slider__value price-slider__value--max" :style="maxLabelStyle">
              $ {{ Math.round(currentMax) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';

  const props = defineProps({
    priceRange: {
      type: Object,
      default: () => ({ min: 0, max: 1000 }),
    },
    appliedPriceRange: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits(['price-change']);

  const isActive = ref(true);

  const rangeMin = computed(() => Math.floor(props.priceRange.min || 0));
  const rangeMax = computed(() => Math.ceil(props.priceRange.max || 1000));

  const currentMin = ref(rangeMin.value);
  const currentMax = ref(rangeMax.value);

  const isRefined = computed(() => {
    return currentMin.value > rangeMin.value || currentMax.value < rangeMax.value;
  });

  const span = computed(() => rangeMax.value - rangeMin.value || 1);

  const fillStyle = computed(() => {
    const leftPct = ((currentMin.value - rangeMin.value) / span.value) * 100;
    const rightPct = 100 - ((currentMax.value - rangeMin.value) / span.value) * 100;
    return { left: `${leftPct}%`, right: `${rightPct}%` };
  });

  const minLabelStyle = computed(() => {
    const pct = ((currentMin.value - rangeMin.value) / span.value) * 100;
    return { left: `${pct}%`, transform: `translateX(-${pct}%)` };
  });

  const maxLabelStyle = computed(() => {
    const pct = ((currentMax.value - rangeMin.value) / span.value) * 100;
    return { left: `${pct}%`, transform: `translateX(-${pct}%)` };
  });

  const MIN_GAP = 20;

  const onMinInput = (e) => {
    const val = Number(e.target.value);
    currentMin.value = Math.max(rangeMin.value, Math.min(val, currentMax.value - MIN_GAP));
    e.target.value = currentMin.value;
    commitRange();
  };

  const onMaxInput = (e) => {
    const val = Number(e.target.value);
    currentMax.value = Math.min(rangeMax.value, Math.max(val, currentMin.value + MIN_GAP));
    e.target.value = currentMax.value;
    commitRange();
  };

  let debounceTimer = null;

  const commitRange = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const min = currentMin.value <= rangeMin.value ? undefined : currentMin.value;
      const max = currentMax.value >= rangeMax.value ? undefined : currentMax.value;

      if (min === undefined && max === undefined) {
        emit('price-change', []);
      } else {
        emit('price-change', [min ?? rangeMin.value, max ?? rangeMax.value]);
      }
    }, 300);
  };

  watch(() => props.priceRange, (newRange) => {
    const newMin = Math.floor(newRange.min || 0);
    const newMax = Math.ceil(newRange.max || 1000);

    if (!isRefined.value) {
      currentMin.value = newMin;
      currentMax.value = newMax;
    }
  });

  watch(() => props.appliedPriceRange, (val) => {
    if (!val || !val.length) {
      currentMin.value = rangeMin.value;
      currentMax.value = rangeMax.value;
    } else if (val.length === 2) {
      currentMin.value = val[0];
      currentMax.value = val[1];
    }
  }, { immediate: true });
</script>
